import React, { useState } from 'react';
import { SearchIcon, BookIcon, EyeIcon, ThumbsUpIcon, ChevronRightIcon } from 'lucide-react';
import { knowledgeBaseArticles, departments, currentUser } from '../data/mockData';
interface KnowledgeBaseProps {
  initialSearchQuery?: string;
}
export function KnowledgeBase({
  initialSearchQuery = ''
}: KnowledgeBaseProps) {
  const [searchQuery, setSearchQuery] = useState(initialSearchQuery);
  const [activeDepartment, setActiveDepartment] = useState('All');
  const filteredArticles = knowledgeBaseArticles.filter(article => {
    const matchesSearch = article.title.toLowerCase().includes(searchQuery.toLowerCase()) || article.summary.toLowerCase().includes(searchQuery.toLowerCase()) || article.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesDept = activeDepartment === 'All' || article.department === activeDepartment || article.department === 'All';
    return matchesSearch && matchesDept;
  });
  const recommendedArticles = knowledgeBaseArticles.filter(a => a.department === currentUser.department || a.department === 'All').slice(0, 3);
  return <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-60">
      {/* Header & Search */}
      <div className="mb-10" data-id="element-61">
        <h1 className="text-3xl font-bold text-slate-900 mb-6" data-id="element-62">
          Knowledge Base
        </h1>
        <div className="relative max-w-3xl" data-id="element-63">
          <SearchIcon className="absolute left-4 top-3.5 w-5 h-5 text-slate-400" data-id="element-64" />
          <input type="text" className="w-full pl-12 pr-4 py-3 rounded-xl border border-slate-300 shadow-sm focus:ring-2 focus:ring-blue-600 focus:border-transparent outline-none" placeholder="Search articles, guides, and troubleshooting steps..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-65" />
        </div>
      </div>

      {/* Department Filters */}
      <div className="mb-8 overflow-x-auto pb-2" data-id="element-66">
        <div className="flex space-x-2 min-w-max" data-id="element-67">
          {departments.map(dept => <button key={dept} onClick={() => setActiveDepartment(dept)} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${activeDepartment === dept ? 'bg-blue-600 text-white shadow-sm' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'}`} data-id="element-68">
              {dept}{' '}
              {dept === currentUser.department && dept !== 'All' && '(Yours)'}
            </button>)}
        </div>
      </div>

      {/* Recommended Section (Only show if no search query and All is selected) */}
      {!searchQuery && activeDepartment === 'All' && <div className="mb-12" data-id="element-69">
          <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center" data-id="element-70">
            <BookIcon className="w-5 h-5 mr-2 text-blue-600" data-id="element-71" />
            Recommended for {currentUser.department}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-72">
            {recommendedArticles.map(article => <ArticleCard key={`rec-${article.id}`} article={article} data-id="element-73" />)}
          </div>
        </div>}

      {/* Main Results */}
      <div data-id="element-74">
        <h2 className="text-xl font-bold text-slate-900 mb-4" data-id="element-75">
          {searchQuery ? 'Search Results' : 'All Articles'}
          <span className="text-slate-500 text-base font-normal ml-2" data-id="element-76">
            ({filteredArticles.length})
          </span>
        </h2>

        {filteredArticles.length > 0 ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-77">
            {filteredArticles.map(article => <ArticleCard key={article.id} article={article} data-id="element-78" />)}
          </div> : <div className="text-center py-16 bg-white rounded-2xl border border-slate-200 border-dashed" data-id="element-79">
            <BookIcon className="w-12 h-12 text-slate-300 mx-auto mb-4" data-id="element-80" />
            <h3 className="text-lg font-medium text-slate-900 mb-2" data-id="element-81">
              No articles found
            </h3>
            <p className="text-slate-500" data-id="element-82">
              Try adjusting your search terms or department filter.
            </p>
          </div>}
      </div>
    </div>;
}
function ArticleCard({
  article
}: {
  article: any;
}) {
  return <div className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-md transition-shadow flex flex-col h-full cursor-pointer group" data-id="element-83">
      <div className="mb-3 flex items-start justify-between" data-id="element-84">
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-slate-100 text-slate-600" data-id="element-85">
          {article.category}
        </span>
      </div>
      <h3 className="text-lg font-bold text-slate-900 mb-2 group-hover:text-blue-600 transition-colors line-clamp-2" data-id="element-86">
        {article.title}
      </h3>
      <p className="text-sm text-slate-600 mb-4 line-clamp-2 flex-grow" data-id="element-87">
        {article.summary}
      </p>
      <div className="flex items-center justify-between text-xs text-slate-500 mt-auto pt-4 border-t border-slate-100" data-id="element-88">
        <div className="flex items-center space-x-3" data-id="element-89">
          <span className="flex items-center" data-id="element-90">
            <EyeIcon className="w-3.5 h-3.5 mr-1" data-id="element-91" /> {article.views}
          </span>
          <span className="flex items-center text-green-600" data-id="element-92">
            <ThumbsUpIcon className="w-3.5 h-3.5 mr-1" data-id="element-93" /> {article.helpful}%
          </span>
        </div>
        <span className="flex items-center text-blue-600 font-medium group-hover:translate-x-1 transition-transform" data-id="element-94">
          Read <ChevronRightIcon className="w-4 h-4 ml-0.5" data-id="element-95" />
        </span>
      </div>
    </div>;
}